/**
 * logSync — stuurt log-entries naar de server (POST /api/logs) zodat fouten van ALLE
 * gebruikers centraal bij de teamleider landen. Productie-tegenhanger van devLogSink.js.
 *
 * Anti-lus (LETTERLIJK zoals devLogSink.js): deze module roept NOOIT logError/log aan,
 * elke fetch-fout wordt volledig stil opgevangen, fire-and-forget. Een fout bij het
 * versturen van een fout mag nooit een nieuwe fout produceren.
 *
 * Zonder token wordt NIET verstuurd — de entries blijven in de logger-localStorage staan
 * en gaan alsnog mee zodra er wél een token is (zo raken login-scherm-fouten niet kwijt).
 * Welke entries al verstuurd zijn, wordt gespiegeld naar localStorage, zodat een reload
 * niet alles opnieuw stuurt. Een entry waarvan de count is opgehoogd mag opnieuw mee
 * (sig = `id:count`, dezelfde aanpak als sigOf in devLogSink.js).
 */
import { subscribe, getEntries } from './logger.js';
import { stuurLogs } from './api.js';
import { haalVersToken } from '../auth/huidigeGebruiker.js';

const VERSTUURD_KEY = 'sass-rooster-logs-verstuurd-v1';
const DEBOUNCE_MS = 2000;
const BATCH_MAX = 50;
const VERSTUURD_CAP = 1000; // ruim boven de 200-cap van de logger; houdt localStorage begrensd

// sig = `${id}:${count}` — een opgehoogde count telt als een nieuwe te-versturen staat.
const verstuurd = laadVerstuurd(); // Set<string>
let timer = null;
let bezig = false;
let gestart = false;

function laadVerstuurd() {
  try {
    const raw = localStorage.getItem(VERSTUURD_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function bewaarVerstuurd() {
  try {
    // Set-iteratie = invoegvolgorde → de laatste VERSTUURD_CAP zijn de meest recente sigs.
    const arr = [...verstuurd].slice(-VERSTUURD_CAP);
    localStorage.setItem(VERSTUURD_KEY, JSON.stringify(arr));
  } catch {
    /* stil — nooit via de logger */
  }
}

function sigOf(entry) {
  return entry ? `${entry.id}:${entry.count}` : null;
}

/** Plan een gedempte flush (2s). Snel achter elkaar loggen → één flush. */
function plan() {
  if (timer !== null) return;
  try {
    timer = setTimeout(() => { timer = null; flush(); }, DEBOUNCE_MS);
  } catch {
    timer = null;
  }
}

/**
 * Verstuur alle nog-niet-verstuurde entries in batches (max 50). GEEN token → wachten:
 * de entries blijven staan en gaan mee bij een volgende poging. Faalt altijd stil.
 */
async function flush() {
  if (bezig) return;
  bezig = true;
  try {
    const token = await haalVersToken();
    if (!token) return; // geen token → niet versturen, gewoon wachten

    let entries;
    try { entries = getEntries(); } catch { entries = []; }
    const teVersturen = entries.filter((e) => e && !verstuurd.has(sigOf(e)));
    if (teVersturen.length === 0) return;

    for (let i = 0; i < teVersturen.length; i += BATCH_MAX) {
      const batch = teVersturen.slice(i, i + BATCH_MAX);
      const ok = await stuurLogs(batch); // kale, stille POST → true/false, gooit nooit
      if (!ok) break;                    // mislukt → niet markeren; volgende flush probeert opnieuw
      for (const e of batch) verstuurd.add(sigOf(e));
      bewaarVerstuurd();
    }
  } catch {
    // Anti-lus: een fout hier mag nooit doorgegooid of gelogd worden.
  } finally {
    bezig = false;
  }
}

/**
 * Start de sink. Idempotent. Abonneert op de logger en probeert bij startup één keer te
 * flushen (er kunnen al entries van een vorige sessie in localStorage staan).
 */
export function installLogSync() {
  if (gestart || typeof window === 'undefined') return;
  gestart = true;
  subscribe(plan);
  plan();
}

/** Extern triggerbaar: zodra de app een token krijgt (useRol) meteen proberen te flushen. */
export function flushLogsNu() {
  plan();
}

// Activering: één keer importeren in de app-entry (main.jsx), net als serverSync.js.
installLogSync();
